import React, { useState } from "react";
import { toast } from "react-toastify";
import { OutlinedInput, InputAdornment } from "@material-ui/core";

const formatCurrency = (
    value,
    showDollarSymbol = false,
    showPoundSymbol = false,
    showPercentageIcon = false,
    showNumberOnly = false,
    fractionDigits = null,
) => {
    const resolvedFractionDigits = fractionDigits ?? (showPercentageIcon ? 2 : (showNumberOnly ? 0 : 2));

    if (value == null || value === '') {
        if (showDollarSymbol) {
            return `$${(0).toLocaleString('en-US', {
                minimumFractionDigits: resolvedFractionDigits,
                maximumFractionDigits: resolvedFractionDigits,
            })}`;
        }
        if (showPoundSymbol) {
            return `£${(0).toLocaleString('en-US', {
                minimumFractionDigits: resolvedFractionDigits,
                maximumFractionDigits: resolvedFractionDigits,
            })}`;
        }
        if (showPercentageIcon) {
            return `${(0).toLocaleString('en-US', {
                minimumFractionDigits: resolvedFractionDigits,
                maximumFractionDigits: resolvedFractionDigits,
            })}%`;
        }
        if (showNumberOnly) {
            return '0';
        }
        return (0).toLocaleString('en-US', {
            minimumFractionDigits: resolvedFractionDigits,
            maximumFractionDigits: resolvedFractionDigits,
        });
    }

    const number = Number(String(value).replace(/[$£%,\s]/g, ''));
    if (Number.isNaN(number)) {
        return value;
    }

    const formattedNumber = number.toLocaleString('en-US', {
        minimumFractionDigits: resolvedFractionDigits,
        maximumFractionDigits: resolvedFractionDigits,
    });

    if (showPercentageIcon) {
        return `${formattedNumber}%`;
    }

    if (showDollarSymbol) {
        return `$${formattedNumber}`;
    }

    if (showPoundSymbol) {
        return `£${formattedNumber}`;
    }

    if (showNumberOnly) {
        return formattedNumber;
    }

    return formattedNumber;
};

const CustomNumberInputField = (props) => {
    const { classes, element, dataObj, onChange, styles, inputClasses } = props;

    const key = element.value || element.name;
    const rawValue = dataObj[key] ?? '';
    const [isEditing, setIsEditing] = useState(false);
    const [inputValue, setInputValue] = useState(rawValue);

    const handleFocus = () => {
        setIsEditing(true);
        // Remove formatting when entering edit mode
        const numericValue = rawValue.toString().replace(/[^0-9.-]/g, '');
        setInputValue(numericValue);
    };

    const handleBlur = () => {
        setIsEditing(false);
        // Format the value when leaving edit mode
        let numericValue = parseFloat(inputValue) || 0;
        onChange(key, numericValue, element.type);
    };

    const handleChange = (e) => {
        let val = e.target.value;
        // Allow only numbers, decimal point, and minus sign
        val = val.replace(/[^0-9.-]/g, '');
        // Ensure only one decimal point
        const parts = val.split('.');
        if (parts.length > 2) {
            val = parts[0] + '.' + parts.slice(1).join('');
        }
        const maxLen = element?.maxLength || 100;
        if (maxLen != null && maxLen !== "" && val.length > Number(maxLen)) {
          toast.error(`Value cannot be greater than ${maxLen}`);
          return; 
        }
        setInputValue(val);
    };

    const valueToDisplay = () => {
        if (isEditing) {
            return inputValue;
        }

        return formatCurrency(
            rawValue,
            element.showDollarSymbol,
            element.showPoundSymbol,
            element.showPercentageIcon,
            element.showNumberOnly,
            element.amountFractionDigits,
        );
    };

    const getIcon = () => {
        if (element.showIcon) {
            if (element.showDollarSymbol) {
                return '$';
            } else if (element.showPoundSymbol) {
                return '£';
            } else if (element.showPercentageIcon) {
                return '%';
            } else {
                return '₹'; // Default icon if no specific currency symbol
            }
        }
        return null;
    };

    return (
        <OutlinedInput
            id={element.name}
            variant="outlined"
            required={element.required}
            key={key}
            style={{
                width: "100%",
                height: "32px",
                padding: '5px 8px 6px 8px'
            }}
            classes={{
                root: inputClasses?.smallOutlinedInput,
                notchedOutline: inputClasses?.smallNotchedOutline,
            }}
            name={key}
            disabled={element.readOnly}
            type="text"
            value={valueToDisplay()}
            title={valueToDisplay()}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
        />
    );
};

export default CustomNumberInputField;